import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { isDetachedTask, isOpenTask, lastStationUpdate, MOOD_TEXT, taskMood } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { IssueDetail } from "../../types";
import { LaneBadge } from "../lane-badge";
import { MergeTicket } from "../merge-ticket";
import { ColorDot, StatusBadge } from "../status-badge";
import { LoopBudgetRow } from "../tasks/loop-budget";
import { TimeAgo } from "../time-ago";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../ui/card";
import { HumanHoldBlock } from "./human-hold";
import { QuickFixDialog } from "./quick-fix";
import { SendToSkynetDialog } from "./send-to-skynet";

/**
 * The issue's place on the factory line: the station it is at, and the merge
 * ticket once the Merge agent has queued the pull request, with the action
 * that ticket needs (mark ready, or approve and merge).
 */
export function FactoryCard({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const { agentById, layout } = useFactory();
  const taskQuery = useQuery({
    queryKey: ["task", issue.taskId],
    queryFn: () => api.task(issue.taskId!),
    enabled: Boolean(issue.taskId),
    refetchInterval: 8000,
  });
  const task = taskQuery.data?.task;
  const run = taskQuery.data?.currentRun ?? undefined;
  const merge = taskQuery.data?.mergeQueue ?? null;
  const loopBudget = taskQuery.data?.loopBudget;
  const stationId = issue.currentAgentId ?? task?.currentAgentId ?? run?.agentId;
  const station = stationId ? agentById.get(stationId) : undefined;
  const mood = task && isOpenTask(task) ? taskMood(task, run, merge ?? undefined) : null;
  const showMerge = merge && merge.status !== "cancelled";

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm">Factory</CardTitle>
        {task && (
          <CardAction>
            <StatusBadge status={task.status} />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-3 px-4">
        {issue.taskId ? (
          <>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {station ? (
                  <>
                    <ColorDot color={station.station.color} />
                    At the {station.name} station
                  </>
                ) : (
                  <span className="text-muted-foreground">On the line</span>
                )}
                {task && <LaneBadge task={task} quiet />}
              </div>
              {task && (
                <p className="text-xs text-muted-foreground">
                  {mood ? `${MOOD_TEXT[mood]} · updated` : "Updated"}{" "}
                  <TimeAgo at={lastStationUpdate(task, taskQuery.data?.runs ?? [])} />
                </p>
              )}
            </div>
            {task && loopBudget && !isDetachedTask(task, layout) && (
              <LoopBudgetRow
                budget={loopBudget}
                target={{ taskId: task.id, issueRef: issue.identifier }}
                open={isOpenTask(task)}
                onChanged={onChanged}
              />
            )}
            {showMerge && <MergeTicket item={merge} className="p-3" onDone={onChanged} />}
            <HumanHoldBlock issue={issue} stationName={station?.name} onChanged={onChanged} />
            {issue.prUrl && <QuickFixDialog issue={issue} onChanged={onChanged} />}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Not on the factory line yet. Send it to Skynet to start intake.
          </p>
        )}
        <SendToSkynetDialog issue={issue} onChanged={onChanged} />
      </CardContent>
    </Card>
  );
}
